import { EVENTS, simulationDeepDiveEventBus } from "@/inspection-events"
import {
  deriveYesNoString,
  yesNoBadgeColorClasses,
  yesNoBadgeShapeClasses,
} from "@/metrics/metric-display"
import type { AudioRegionReviewConfig } from "@/metrics/runtime"

import { logger } from "@/lib/logger"
import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type { MetricOutput } from "@/types/api/MetricOutput"
import type { ChartBarFilter } from "@/types/ChartBarFilter"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
  SubvalueByTimestamp,
} from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"

import { SingleFloat } from "../../components/single/SingleFloat"
import { BinaryMetricRecord } from "../base/BinaryMetricRecord"
import { parseResultJson } from "../helpers/parseResultJson"
import { WithAudioFilterMixin } from "../mixins/WithAudioFilterMixin"

const log = logger("AgentNeedsRepromptingMetric")

const CATEGORY = "agent_needs_reprompting"

interface RepromptEvent {
  type: "agent_needs_reprompting"
  start: number
  end: number
  gap: number
  explanation: string
}

interface AgentNeedsRepromptingExplanation {
  metadata: {
    count: number
    duration: number
  }
  reprompt_events: RepromptEvent[]
}

const REPROMPT_STRING_VALUE = "agent_needs_reprompting"

// Build per-event list from subvalues_by_timestamp; falls back to legacy result_json.
function buildRepromptEvents(mo: RunMetricOutput): RepromptEvent[] {
  const subvalues: SubvalueByTimestamp[] = mo.subvalues_by_timestamp ?? []
  const matching = subvalues.filter(
    (sv) => sv.string_value === REPROMPT_STRING_VALUE,
  )
  if (matching.length > 0) {
    return matching.map((sv) => {
      const start = sv.start_offset ?? sv.timestamp ?? 0
      const gap = typeof sv.float_value === "number" ? sv.float_value : 0
      const end = sv.end_offset ?? start + gap
      return {
        type: "agent_needs_reprompting",
        start,
        end,
        gap,
        explanation: `Agent needed reprompting after a ${gap.toFixed(1)}s gap.`,
      }
    })
  }
  const legacy = parseResultJson<AgentNeedsRepromptingExplanation>(mo)
  return legacy?.reprompt_events ?? []
}

// Total event count: prefer typed result.count, then subvalue matches, then legacy metadata.
function getRepromptCount(mo: RunMetricOutput): number {
  const typedCount = (mo.result as { count?: unknown } | null | undefined)
    ?.count
  if (typeof typedCount === "number" && Number.isFinite(typedCount)) {
    return Math.max(0, Math.floor(typedCount))
  }
  const subvalues: SubvalueByTimestamp[] = mo.subvalues_by_timestamp ?? []
  const fromSubvalues = subvalues.filter(
    (sv) => sv.string_value === REPROMPT_STRING_VALUE,
  ).length
  if (fromSubvalues > 0) {
    return fromSubvalues
  }
  const legacy = parseResultJson<AgentNeedsRepromptingExplanation>(mo)
  const raw = legacy?.metadata?.count
  const n = Number(raw)
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0
}

export class AgentNeedsRepromptingMetricRecord extends WithAudioFilterMixin(
  BinaryMetricRecord,
) {
  invertYesNoColors = true

  getBinaryAggregateOutputs(mo: RunMetricOutput[]): RunMetricOutput[] {
    return mo.map((m) => this._toYesNo(m))
  }

  private _toYesNo(mo: RunMetricOutput): MetricOutput {
    const count = getRepromptCount(mo)
    return {
      organization_id: mo.organization_id ?? "",
      simulation_output_id: mo.simulation_output_id,
      metric_id: mo.metric_id ?? this.id,
      output_type: mo.output_type,
      float_value: count > 0 ? 1 : 0,
      string_value: mo.string_value,
      set_value: mo.set_value,
      subvalues_by_timestamp: mo.subvalues_by_timestamp,
      result_json: mo.result_json,
      result: mo.result ?? null,
      created_at: mo.created_at ?? "",
      last_updated_at: mo.last_updated_at ?? "",
      start_time: mo.start_time ?? "",
      end_time: mo.end_time ?? "",
      error_message: mo.error_message,
      error_status: mo.error_status,
      runtime_metadata: mo.runtime_metadata ?? {},
    }
  }

  private _renderCountChip(count: number): React.ReactNode {
    return (
      <span
        className={`${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses.default}`}
      >
        {count} {count === 1 ? "event" : "events"}
      </span>
    )
  }

  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    return this._renderCountChip(getRepromptCount(mo))
  }

  getOverviewNode(
    mo: RunMetricOutput[],
    controlledProps?: {
      includeUnknowns?: boolean
      onIncludeUnknownsChange?: (value: boolean) => void
      hideCheckbox?: boolean
    },
  ): React.ReactNode {
    return super.getOverviewNode(
      this.getBinaryAggregateOutputs(mo),
      controlledProps,
    )
  }

  getHeaderValue(mo: RunMetricOutput[]): string | null {
    // % of calls that needed reprompting (count > 0); 0% is healthy.
    return this.getFailureRateHeaderValue(this.getBinaryAggregateOutputs(mo))
  }

  renderInDepthRunReview(
    metricOutputs: RunMetricOutput[],
    context?: {
      runRows?: RunRowLike[]
      highlightValue?: number | string | null
      onBarClick?: (filter: { type: "exact"; value: string }) => void
      activeFilters?: ChartBarFilter[]
    },
  ): React.ReactNode {
    const transformed = metricOutputs.map((m) => {
      const toYesNo = this._toYesNo(m)
      const derived = deriveYesNoString(toYesNo)
      // Suppress chip for UNKNOWN outputs.
      return {
        ...toYesNo,
        string_value: derived === "UNKNOWN" ? null : derived,
      }
    })
    return super.renderInDepthRunReview(transformed, context)
  }

  getSingleMetricOutputNode(mo: RunMetricOutput): React.ReactNode {
    const totalCount = getRepromptCount(mo)
    return (
      <SingleFloat
        mo={{ ...mo, float_value: totalCount }}
        units="events"
        formatFn={(v) => String(v)}
      />
    )
  }
  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    try {
      const formatTime = (s: number) =>
        `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`

      const events = buildRepromptEvents(mo)
      const totalCount = getRepromptCount(mo)
      const handleSeekToTimestamp = (timestamp: number) => {
        simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_SEEK, {
          time: timestamp,
        })
      }

      const totalRepromptTime = events.reduce(
        (sum: number, e: RepromptEvent) => sum + e.gap,
        0,
      )

      const avgGap = totalCount > 0 ? totalRepromptTime / totalCount : 0
      const longestGap = events.reduce(
        (max: number, e: RepromptEvent) => Math.max(max, e.gap),
        0,
      )

      return (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-coval-border bg-surface-default rounded-lg p-4">
              <div className="text-sm text-coval-text-muted font-medium mb-1">
                Total Events
              </div>
              <div className="text-2xl font-bold text-coval-text">
                {totalCount}
              </div>
            </div>

            <div className="border border-coval-border bg-surface-default rounded-lg p-4">
              <div className="text-sm text-coval-text-muted font-medium mb-1">
                Total Reprompt Time
              </div>
              <div className="text-2xl font-bold text-coval-text">
                {totalRepromptTime.toFixed(1)}s
              </div>
            </div>

            <div className="border border-coval-border bg-surface-default rounded-lg p-4">
              <div className="text-sm text-coval-text-muted font-medium mb-1">
                Average Gap
              </div>
              <div className="text-2xl font-bold text-coval-text">
                {avgGap.toFixed(1)}s
              </div>
            </div>

            <div className="border border-coval-border bg-surface-default rounded-lg p-4">
              <div className="text-sm text-coval-text-muted font-medium mb-1">
                Longest Gap
              </div>
              <div className="text-2xl font-bold text-coval-text">
                {longestGap.toFixed(1)}s
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4 text-coval-text">
              Reprompt Events
            </h3>
            <div className="space-y-3">
              {events.map((event, i) => {
                const range = `${formatTime(event.start)} – ${formatTime(event.end)}`

                return (
                  <button
                    type="button"
                    key={i}
                    className="block w-full text-left border border-coval-border rounded-lg p-4 bg-surface-default cursor-pointer"
                    onClick={() => handleSeekToTimestamp(event.start)}
                  >
                    <span className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 rounded text-xs font-semibold border border-coval-border bg-surface-default text-coval-text">
                        Agent Needs Reprompting
                      </span>
                      <span className="text-sm text-coval-text-muted font-mono">
                        {range}
                      </span>
                      <span className="text-sm font-bold text-orange-600">
                        {event.gap.toFixed(1)}s gap
                      </span>
                    </span>
                    <span className="block text-sm text-coval-text-muted leading-relaxed">
                      {event.explanation}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          {totalCount === 0 && (
            <div className="text-center py-8 text-coval-text-muted">
              No reprompting events detected
            </div>
          )}
        </div>
      )
    } catch (error) {
      log.error(
        "Error parsing explanation for agent needs reprompting metric",
        error,
      )
      return <div className="text-red-500">Error displaying metric details</div>
    }
  }

  renderMetricType(): React.ReactNode {
    return "Audio Metric"
  }

  getAudioRegions(
    mo: MetricOutputDisplaySource | undefined,
  ): AudioRegionParams[] {
    if (!mo) {
      return []
    }

    try {
      // Use subvalues_by_timestamp for waveform regions (preferred source)
      const subvalues: SubvalueByTimestamp[] = mo.subvalues_by_timestamp ?? []
      if (subvalues && subvalues.length > 0) {
        return subvalues.map((sv, i) => ({
          id: `reprompt:${i}`,
          // @deprecated fallback: sv.timestamp — remove after migration to start_offset/end_offset
          start: sv.start_offset ?? sv.timestamp ?? 0,
          end: sv.end_offset ?? (sv.timestamp ?? 0) + 0.5,
          color: "rgba(255, 140, 0, 0.3)",
          drag: false,
          resize: false,
        }))
      }

      // @deprecated fallback: result_json.reprompt_events — remove after subvalues_by_timestamp migration is complete
      const explanation = parseResultJson<AgentNeedsRepromptingExplanation>(mo)
      return (explanation?.reprompt_events ?? [])
        .filter((e) => typeof e.start === "number" && typeof e.end === "number")
        .map((e, i) => ({
          id: `reprompt:${i}`,
          start: e.start,
          end: e.end,
          color: "rgba(255, 140, 0, 0.3)",
          drag: false,
          resize: false,
        }))
    } catch (err) {
      log.error(
        "Failed to parse agent needs reprompting regions",
        err instanceof Error ? err : undefined,
      )
      return []
    }
  }

  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return {
      category: CATEGORY,
      scoreType: "raw_count",
      useRegions: true,
      buildReviewedResultJson: ({ editedCount, durationSeconds }) => ({
        metadata: {
          count: editedCount,
          duration:
            durationSeconds != null ? Number(durationSeconds.toFixed(2)) : null,
        },
      }),
    }
  }

  renderValueOnlyTableCell(
    value: number | string,
    _metricErrorStatus: string,
    _truncatedExplanation: string,
    _errorMessage?: string,
    _organizationId?: string,
  ): React.ReactNode {
    let count: number | null = null
    if (
      typeof value === "number" &&
      Number.isFinite(value) &&
      Number.isInteger(value) &&
      value >= 0
    ) {
      count = value
    } else if (typeof value === "string" && /^\d+$/.test(value)) {
      count = parseInt(value, 10)
    }

    return (
      <div className="flex items-center justify-center">
        {count != null ? this._renderCountChip(count) : "—"}
      </div>
    )
  }
}
